import { injectable } from 'inversify';

import {
  CreateVacationScheduleRequest,
  VacationValidators,
} from '@app/gen/vacations';

import { CreateVacationDto } from '../../dto';

type UnitType = 'calendar' | 'work';

@injectable()
export class VacationCreateMapper {
  public transform(
    createVacationDto: CreateVacationDto,
  ): CreateVacationScheduleRequest {
    const validators: VacationValidators = {
      must_schedule_all_days: createVacationDto.mustScheduleAllDays,
      must_schedule_14days: createVacationDto.mustSchedule14days,
      limit_to_calculated_days: createVacationDto.limitToCalculatedDays,
      min_5_days_between_not_workdays:
        createVacationDto.min5DaysBetweenNotWorkdays,
      ...(createVacationDto.weekendExclusion
        ? { weekend_exclusion: createVacationDto.weekendExclusion }
        : {}),
      ...(createVacationDto.minSpecificWeekendDays
        ? {
            min_specific_weekend_days: createVacationDto.minSpecificWeekendDays,
          }
        : {}),
      working_day: createVacationDto.workingDay,
    };

    if (createVacationDto.minDuration) {
      validators.min_duration = {
        days: createVacationDto.minDuration
          ? Number(createVacationDto.minDurationDays) || 1
          : 1,
        unit: createVacationDto.minDurationUnit as UnitType,
      };
    }

    if (createVacationDto.maxDuration) {
      validators.max_duration = {
        days: createVacationDto.maxDuration
          ? Number(createVacationDto.maxDurationDays) || 1
          : 1,
        unit: createVacationDto.maxDurationUnit as UnitType,
      };
    }

    if (createVacationDto.daysMustBeDivisibleBy) {
      validators.days_must_be_divisible_by =
        Number(createVacationDto.daysMustBeDivisibleByNumber) || 1;
    }

    const validatorVacationTypes =
      createVacationDto.validatorGroups &&
      createVacationDto.validatorGroups.length > 0
        ? createVacationDto.validatorGroups.reduce((acc, validatorGroup) => {
            if (
              validatorGroup.minDuration ||
              validatorGroup.maxDuration ||
              validatorGroup.mustSchedule14days ||
              validatorGroup.min5DaysBetweenNotWorkdays ||
              validatorGroup.minSpecificWeekendDays
            ) {
              // @ts-ignore
              acc[`${validatorGroup.vacationType}`] = {
                ...(validatorGroup.minDuration && validatorGroup.minDurationDays
                  ? {
                      min_duration: {
                        days: Number(validatorGroup.minDurationDays),
                        unit: validatorGroup.minDurationUnit,
                      },
                    }
                  : {}),
                ...(validatorGroup.maxDuration && validatorGroup.maxDurationDays
                  ? {
                      max_duration: {
                        days: Number(validatorGroup.maxDurationDays),
                        unit: validatorGroup.maxDurationUnit,
                      },
                    }
                  : {}),
                ...(validatorGroup.mustSchedule14days
                  ? { must_schedule_14days: true }
                  : {}),
                ...(validatorGroup.min5DaysBetweenNotWorkdays
                  ? { min_5_days_between_not_workdays: true }
                  : {}),
                ...(validatorGroup.minSpecificWeekendDays
                  ? { min_specific_weekend_days: true }
                  : {}),
              };
            }

            return acc;
          }, {})
        : null;

    const requestBody: CreateVacationScheduleRequest = {
      company_ids: createVacationDto.companyIds,
      year: new Date().getFullYear() + 1,
      active_to: createVacationDto.activeTo,
      allow_employee_comment: JSON.parse(
        createVacationDto.allowEmployeeComment,
      ),
      send_to_1c: true,
      add_previous_vacation: JSON.parse(createVacationDto.addPreviousVacation),
      validators,
      ...(validatorVacationTypes
        ? { validator_vacation_types: validatorVacationTypes }
        : {}),
      employee_deadline: createVacationDto.employeeDeadline,
      skip_missing_managers: JSON.parse(createVacationDto.skipMissingManagers),
      ...(createVacationDto.unit_items
        ? { unit_items: createVacationDto.unit_items }
        : {}),
      approvers:
        createVacationDto.approvers?.map(
          ({
            canEdit,
            deadline,
            groupName,
            managerApproveLevels,
            orgstructureType,
            approvalType,
          }) => ({
            can_edit: JSON.parse(canEdit),
            group_approve:
              groupName && approvalType === 'groups'
                ? { group_name: groupName }
                : undefined,
            manager_approve:
              approvalType === 'manager'
                ? {
                    type: orgstructureType,
                    approve_levels: Number(managerApproveLevels) || 1,
                  }
                : undefined,
            assistant_approve:
              approvalType === 'assistant'
                ? {
                    type: orgstructureType as 'operational',
                    approve_levels: Number(managerApproveLevels) || 1,
                  }
                : undefined,

            deadline,
          }),
        ) ?? [],
    };

    if (createVacationDto.companyComment?.length) {
      requestBody.company_comment = createVacationDto.companyComment;
    }

    if (createVacationDto.teammateAccess) {
      requestBody.teammate_access = createVacationDto.teammateAccess;
    }

    return requestBody;
  }
}
